// src/components/document/VariableInsertMenu.tsx
// Dropdown menu for inserting header/footer template variables.

import { useState, useRef, useEffect } from 'react';
import { useI18n } from '@react/hooks';
import { HEADER_FOOTER_VARIABLES } from '@/features/document/logic';

interface VariableInsertMenuProps {
  onInsert: (variable: string) => void;
}

export function VariableInsertMenu({ onInsert }: VariableInsertMenuProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={menuRef} className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        title={t('headerFooter.insertVariable')}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3 h-3">
          <path d="M12 5v14" strokeLinecap="round" /><path d="M5 12h14" strokeLinecap="round" />
        </svg>
        {'{x}'}
      </button>

      {open && (
        <div className="absolute bottom-full mb-1 left-0 z-50 w-56 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
          <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider border-b border-border">
            {t('headerFooter.variables')}
          </div>
          <div className="max-h-48 overflow-y-auto py-1">
            {HEADER_FOOTER_VARIABLES.map((v) => (
              <button
                key={v.key}
                onClick={() => {
                  onInsert(v.key);
                  setOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted transition-colors"
              >
                <div>
                  <span className="text-sm text-foreground font-mono">{v.key}</span>
                  <p className="text-xs text-muted-foreground">{t(v.labelKey)}</p>
                </div>
                <span className="text-xs text-muted-foreground/60 font-mono">{v.example}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
